import { BaseGameLayer } from '../../engine/GameLayer';
import { Portal, TransferableEntity } from '@antification/protocol';
import {
  Ant,
  AntRole,
  AntRoleAttributes,
  AntState,
  AntTask,
  BroodStage,
  BroodSystemConfig,
  Chamber,
  ChamberType,
  ColonySim,
  ColonySnapshot,
  ColonyStats,
  DamageConfig,
  EventBus,
  GameEventTypes,
  MetabolismConfig,
  RoleTransitionConfig,
  StimulusConfig,
  TunnelNetwork,
} from '@antification/core';
import { Dimensions } from '../../engine/GameCanvas';
import { AntRenderer, AntRenderContext } from './rendering/AntRenderer';
import { AntWebGLRenderer } from '../../rendering/AntWebGLRenderer';

/**
 * Formicarium layer — the ant colony, as the player sees it.
 *
 * The *client* half of a colony: a camera, a canvas, the ant a player clicked
 * on, and a renderer over a `ColonySim`. Everything that decides something lives
 * in the sim, which is why this file may touch `document`, WebGL and the wall
 * clock freely and the colony still runs identically in a server with none of
 * them.
 */
export class FormicariumLayer extends BaseGameLayer {
  readonly id: string;
  readonly displayName: string;
  readonly colonyIndex: number;

  /** The deterministic simulation this layer draws and drives. */
  readonly sim: ColonySim;

  // Cached gradients for performance (avoid recreating every frame)
  private backgroundGradient: CanvasGradient | null = null;
  private cachedGradientHeight = 0;

  // Renderers (separated for cleaner architecture)
  private antRenderer = new AntRenderer();
  private webglRenderer: AntWebGLRenderer | null = null;
  private webglInitialized = false;
  private useWebGL = false; // Disabled - WebGL doesn't help here, bottleneck is tunnels/chambers

  // View state
  private offsetX = 0;
  private offsetY = 0;
  private scale = 1;
  private baseScale = 1;
  private hoveredAnt: Ant | null = null;
  private selectedAnt: Ant | null = null;

  // Mouse tracking
  private mouseX = 0;
  private mouseY = 0;

  // Zoom + pan state (aligned with Parkington interactions)
  private readonly MIN_SCALE_FACTOR = 0.5;
  private readonly MAX_SCALE_FACTOR = 3;
  private readonly WHEEL_ZOOM_FACTOR = 0.1;
  private readonly SPACE_ZOOM_FACTOR = 2;
  private readonly PINCH_ZOOM_SENSITIVITY = 1.5;
  private isPanMode = false;
  private isMouseDown = false;
  private isPanning = false;
  private suppressClick = false;
  private panStart: { x: number; y: number } | null = null;
  private lastTouchDistance: number | null = null;
  private lastTouchCenter: { x: number; y: number } | null = null;
  private wasTwoFingerGesture = false;
  private twoFingerGestureEndTime = 0;
  private readonly TWO_FINGER_COOLDOWN = 200;
  private touchIsDragging = false;
  private hasUserTransformed = false;

  constructor(events: EventBus, colonyIndex: number = 0, worldSeed: string = 'parkington') {
    super(events);

    this.sim = new ColonySim(events, colonyIndex, worldSeed);
    this.colonyIndex = colonyIndex;
    this.id = this.sim.id;
    this.displayName = `Colony ${colonyIndex + 1}`;

    // Listen for colony selection to show/hide WebGL canvas
    this.events.on(GameEventTypes.COLONY_SELECTED, (payload) => {
      const data = payload as { colonyIndex: number };
      this.setWebGLVisible(data.colonyIndex === this.colonyIndex);
    });
  }

  /**
   * The colony digs itself, then the client wires itself to it.
   *
   * The `typeof document !== 'undefined'` guards these listeners used to carry
   * are gone, and their absence is the point: a headless run constructs a
   * `ColonySim` and never reaches this file, so there is no longer a browserless
   * caller to defend against.
   */
  async init(): Promise<void> {
    // Nothing below this line may change what the colony decided. Input binding
    // is the client's business alone.
    this.sim.init();

    const bottomCanvas = document.getElementById('colony-canvas');
    if (bottomCanvas) {
      bottomCanvas.addEventListener('mousemove', this.handleMouseMove.bind(this));
      bottomCanvas.addEventListener('mousedown', this.handleMouseDown.bind(this));
      bottomCanvas.addEventListener('mouseup', this.handleMouseUp.bind(this));
      bottomCanvas.addEventListener('mouseleave', this.handleMouseUp.bind(this));
      bottomCanvas.addEventListener('click', this.handleClick.bind(this));
      bottomCanvas.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });

      // Touch support for mobile
      bottomCanvas.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
      bottomCanvas.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
      bottomCanvas.addEventListener('touchend', this.handleTouchEnd.bind(this));
      bottomCanvas.addEventListener('touchcancel', this.handleTouchEnd.bind(this));
    }

    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  handleMouseMove(e: MouseEvent): void {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    this.mouseX = e.clientX - rect.left;
    this.mouseY = e.clientY - rect.top;

    if (this.isPanMode && this.isMouseDown && this.panStart) {
      const dx = this.mouseX - this.panStart.x;
      const dy = this.mouseY - this.panStart.y;
      if (Math.abs(dx) > 0 || Math.abs(dy) > 0) {
        this.offsetX += dx;
        this.offsetY += dy;
        this.panStart = { x: this.mouseX, y: this.mouseY };
        this.isPanning = true;
        this.suppressClick = true;
        this.hasUserTransformed = true;
      }
      return;
    }

    // Check for ant under cursor
    this.hoveredAnt = this.getAntAt(this.mouseX, this.mouseY);
  }

  // Touch support
  private touchStartPos: { x: number; y: number } | null = null;

  handleTouchStart(e: TouchEvent): void {
    this.touchIsDragging = false;

    if (e.touches.length === 2) {
      e.preventDefault();
      this.wasTwoFingerGesture = true;
      this.touchStartPos = null;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      this.lastTouchDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      const rect = (e.target as HTMLElement).getBoundingClientRect();
      this.lastTouchCenter = {
        x: (touch1.clientX + touch2.clientX) / 2 - rect.left,
        y: (touch1.clientY + touch2.clientY) / 2 - rect.top,
      };
    } else if (e.touches.length === 1) {
      if (this.wasTwoFingerGesture && (performance.now() - this.twoFingerGestureEndTime) < this.TWO_FINGER_COOLDOWN) {
        return;
      }
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      const touch = e.touches[0];
      this.touchStartPos = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      };
      if (this.isPanMode) {
        this.panStart = { ...this.touchStartPos };
        this.touchIsDragging = true;
      }
      // Update mouse position for hover detection
      this.mouseX = this.touchStartPos.x;
      this.mouseY = this.touchStartPos.y;
      this.hoveredAnt = this.getAntAt(this.mouseX, this.mouseY);
    }
  }

  handleTouchMove(e: TouchEvent): void {
    if (e.touches.length === 2 && this.lastTouchDistance !== null && this.lastTouchCenter !== null) {
      e.preventDefault();
      this.touchIsDragging = true;
      this.hasUserTransformed = true;

      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const newDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      const rect = (e.target as HTMLElement).getBoundingClientRect();
      const newCenter = {
        x: (touch1.clientX + touch2.clientX) / 2 - rect.left,
        y: (touch1.clientY + touch2.clientY) / 2 - rect.top,
      };

      const panDeltaX = newCenter.x - this.lastTouchCenter.x;
      const panDeltaY = newCenter.y - this.lastTouchCenter.y;
      this.offsetX += panDeltaX;
      this.offsetY += panDeltaY;

      const rawZoomFactor = newDistance / this.lastTouchDistance;
      const zoomFactor = 1 + (rawZoomFactor - 1) * this.PINCH_ZOOM_SENSITIVITY;

      if (zoomFactor < 1 && this.scale <= this.getMinScale()) {
        this.centerView();
      } else {
        this.zoomAtPoint(newCenter.x, newCenter.y, zoomFactor);
      }

      this.lastTouchDistance = newDistance;
      this.lastTouchCenter = newCenter;
      return;
    }

    if (e.touches.length === 1) {
      if (this.wasTwoFingerGesture && (performance.now() - this.twoFingerGestureEndTime) < this.TWO_FINGER_COOLDOWN) {
        return;
      }

      const rect = (e.target as HTMLElement).getBoundingClientRect();
      const touchX = e.touches[0].clientX - rect.left;
      const touchY = e.touches[0].clientY - rect.top;

      if (this.isPanMode) {
        e.preventDefault();
        this.touchIsDragging = true;
        this.hasUserTransformed = true;
        if (this.panStart) {
          const dx = touchX - this.panStart.x;
          const dy = touchY - this.panStart.y;
          this.offsetX += dx;
          this.offsetY += dy;
        }
        this.panStart = { x: touchX, y: touchY };
        return;
      }

      this.mouseX = touchX;
      this.mouseY = touchY;
      this.hoveredAnt = this.getAntAt(this.mouseX, this.mouseY);
    }
  }

  handleTouchEnd(e: TouchEvent): void {
    if (e.touches.length < 2 && this.lastTouchDistance !== null) {
      this.twoFingerGestureEndTime = performance.now();
    }

    if (e.touches.length < 2) {
      this.lastTouchDistance = null;
      this.lastTouchCenter = null;
    }

    if (e.touches.length === 0) {
      this.panStart = null;
    }

    if (e.changedTouches.length === 1 && this.touchStartPos) {
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      const touch = e.changedTouches[0];
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      // Only register as tap if finger didn't move much
      const dx = Math.abs(x - this.touchStartPos.x);
      const dy = Math.abs(y - this.touchStartPos.y);
      if (dx < 20 && dy < 20 && !this.touchIsDragging) {
        // Simulate click at touch position
        this.mouseX = x;
        this.mouseY = y;
        this.hoveredAnt = this.getAntAt(this.mouseX, this.mouseY);
        this.processClick();
      }
    }
    this.touchStartPos = null;
    this.touchIsDragging = false;

    if (e.touches.length === 0 && this.wasTwoFingerGesture) {
      setTimeout(() => { this.wasTwoFingerGesture = false; }, this.TWO_FINGER_COOLDOWN);
    }
  }

  handleClick(e: MouseEvent): void {
    if (this.isPanning || this.suppressClick) {
      this.suppressClick = false;
      return;
    }
    this.processClick();
  }

  handleMouseDown(e: MouseEvent): void {
    this.isMouseDown = true;
    if (this.isPanMode) {
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      this.panStart = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      this.isPanning = false;
    }
  }

  handleMouseUp(): void {
    this.isMouseDown = false;
    this.panStart = null;
    this.isPanning = false;
  }


  private processClick(): void {
    // Check if clicking on the "name this ant" button
    const selected = this.selectedAnt;
    if (selected && !selected.customName && selected !== this.sim.getQueen()) {
      const panelWidth = 220;
      const panelX = this.width - panelWidth - 20;
      const panelY = 60;
      const bio = selected.getBiography();
      const panelHeight = bio.length * 18 + 80; // Extra space for more info
      const btnY = panelY + panelHeight - 30;
      const btnX = panelX + 15;
      const btnWidth = panelWidth - 30;
      const btnHeight = 22;

      if (this.mouseX >= btnX && this.mouseX <= btnX + btnWidth &&
          this.mouseY >= btnY && this.mouseY <= btnY + btnHeight) {
        // Prompt for name. The colony records it; the layer never writes state.
        const newName = prompt(`Name this ${selected.role}:`, selected.name);
        if (newName) {
          this.sim.nameAnt(selected.id, newName);
        }
        return;
      }
    }

    if (this.hoveredAnt) {
      this.selectedAnt = this.hoveredAnt;
    } else {
      this.selectedAnt = null;
    }
  }

  private getAntAt(screenX: number, screenY: number): Ant | null {
    // Convert screen to world coordinates
    const worldX = (screenX - this.offsetX) / this.scale;
    const worldY = (screenY - this.offsetY) / this.scale;

    // Find closest ant within click radius
    let closest: Ant | null = null;
    let closestDist = 15; // Click radius

    for (const ant of this.sim.ants.values()) {
      if (!ant.isAlive()) continue;
      if (this.sim.antsOnSurface.has(ant.id)) continue; // Skip ants on surface

      const dx = ant.position.x - worldX;
      const dy = ant.position.y - worldY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < closestDist) {
        closestDist = dist;
        closest = ant;
      }
    }

    return closest;
  }

  handleWheel(e: WheelEvent): void {
    e.preventDefault();

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const currentScale = this.scale;
    const isZoomingOut = e.deltaY > 0;

    if (isZoomingOut && currentScale <= this.getMinScale()) {
      this.centerView();
      return;
    }

    const zoomFactor = e.deltaY < 0
      ? 1 + this.WHEEL_ZOOM_FACTOR
      : 1 / (1 + this.WHEEL_ZOOM_FACTOR);

    this.zoomAtPoint(mouseX, mouseY, zoomFactor);
  }

  handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'm' || e.key === 'M') {
      this.isPanMode = !this.isPanMode;
      this.panStart = null;
      return;
    }

    if (e.key === ' ') {
      e.preventDefault();
      const targetScale = this.scale > this.baseScale
        ? this.baseScale
        : Math.min(this.baseScale * this.SPACE_ZOOM_FACTOR, this.getMaxScale());
      const zoomFactor = targetScale / this.scale;
      this.zoomAtPoint(this.width / 2, this.height / 2, zoomFactor);
    }
  }

  private zoomAtPoint(screenX: number, screenY: number, zoomFactor: number): void {
    const oldScale = this.scale;
    const newScale = Math.max(this.getMinScale(), Math.min(this.getMaxScale(), oldScale * zoomFactor));
    if (newScale === oldScale) return;

    const worldX = (screenX - this.offsetX) / oldScale;
    const worldY = (screenY - this.offsetY) / oldScale;
    this.scale = newScale;
    this.offsetX = screenX - worldX * newScale;
    this.offsetY = screenY - worldY * newScale;
    this.hasUserTransformed = true;
  }

  private getMinScale(): number {
    return this.baseScale * this.MIN_SCALE_FACTOR;
  }

  private getMaxScale(): number {
    return this.baseScale * this.MAX_SCALE_FACTOR;
  }

  private centerView(): void {
    const scaledWidth = this.sim.tunnelNetwork.width * this.scale;
    const scaledHeight = this.sim.tunnelNetwork.height * this.scale;
    this.offsetX = this.width / 2 - scaledWidth / 2;
    this.offsetY = Math.max(10, (this.height - scaledHeight) / 2);
  }

  render(ctx: CanvasRenderingContext2D): void {
    // Initialize WebGL renderer on first render
    if (this.useWebGL && !this.webglInitialized) {
      this.initializeWebGL(ctx.canvas);
    }

    // Clear with underground colors - use cached gradient for performance
    if (!this.backgroundGradient || this.cachedGradientHeight !== this.height) {
      this.backgroundGradient = ctx.createLinearGradient(0, 0, 0, this.height);
      this.backgroundGradient.addColorStop(0, '#2d1810');
      this.backgroundGradient.addColorStop(0.3, '#1a0f08');
      this.backgroundGradient.addColorStop(1, '#0d0804');
      this.cachedGradientHeight = this.height;
    }
    ctx.fillStyle = this.backgroundGradient;
    ctx.fillRect(0, 0, this.width, this.height);

    ctx.save();
    ctx.translate(this.offsetX, this.offsetY);
    ctx.scale(this.scale, this.scale);

    // Draw tunnels
    this.renderTunnels(ctx);

    // Draw chambers
    this.renderChambers(ctx);

    // Draw brood (eggs, larvae, pupae)
    this.renderBrood(ctx);

    // Draw portals
    this.renderPortals(ctx);

    // Draw ants - use WebGL if available and enabled, otherwise fall back to Canvas 2D
    if (this.useWebGL && this.webglRenderer?.isReady()) {
      this.renderAntsWebGL(ctx);
    } else {
      // Fallback to Canvas 2D sprite cache
      const antRenderContext: AntRenderContext = {
        tickCount: this.sim.tick,
        selectedAnt: this.selectedAnt,
        hoveredAnt: this.hoveredAnt,
        antsOnSurface: this.sim.antsOnSurface,
        isAtTarget: (ant: Ant) => this.sim.isAtTarget(ant),
        getTunnelDirection: (tunnelId: number, movingTowardEnd: boolean) => {
          const tunnel = this.sim.tunnelNetwork.getTunnelById(tunnelId);
          if (!tunnel) return null;
          const fromPos = movingTowardEnd ? tunnel.start : tunnel.end;
          const toPos = movingTowardEnd ? tunnel.end : tunnel.start;
          return { angle: Math.atan2(toPos.y - fromPos.y, toPos.x - fromPos.x) };
        },
      };
      this.antRenderer.renderAnts(ctx, this.sim.ants, antRenderContext);
    }

    // Draw debris particles from damage events
    this.renderDebris(ctx);

    ctx.restore();

    // Draw selected ant info (stays on canvas - contextual overlay)
    if (this.selectedAnt) {
      this.renderAntInfo(ctx, this.selectedAnt);
    } else if (this.hoveredAnt) {
      this.renderAntHover(ctx, this.hoveredAnt);
    }
  }

  /**
   * Initialize WebGL renderer and attach to DOM.
   */
  private initializeWebGL(canvas2D: HTMLCanvasElement): void {
    this.webglInitialized = true;

    this.webglRenderer = new AntWebGLRenderer();
    const webglCanvas = this.webglRenderer.initialize();

    if (!webglCanvas) {
      console.warn('[FormicariumLayer] WebGL not available, using Canvas 2D fallback');
      this.webglRenderer = null;
      return;
    }

    // Position WebGL canvas over the 2D canvas
    const parent = canvas2D.parentElement;
    if (parent) {
      webglCanvas.style.position = 'absolute';
      webglCanvas.style.top = '0';
      webglCanvas.style.left = '0';
      webglCanvas.style.pointerEvents = 'none';
      parent.style.position = 'relative';
      parent.appendChild(webglCanvas);

      // Match size
      this.webglRenderer.resize(this.width, this.height, window.devicePixelRatio || 1);

      console.log('[FormicariumLayer] WebGL canvas attached to:', parent);
      console.log('[FormicariumLayer] Canvas size:', this.width, 'x', this.height);
    }

    // Expose for debugging
    (window as any).antWebGLRenderer = this.webglRenderer;
    // Global toggle to disable WebGL for A/B comparison
    (window as any).disableWebGL = () => {
      this.useWebGL = false;
      if (this.webglRenderer) {
        this.webglRenderer.getCanvas().style.display = 'none';
      }
      console.log('[FormicariumLayer] WebGL disabled - using Canvas 2D fallback');
    };
    (window as any).enableWebGL = () => {
      this.useWebGL = true;
      if (this.webglRenderer) {
        this.webglRenderer.getCanvas().style.display = 'block';
      }
      console.log('[FormicariumLayer] WebGL enabled');
    };

    console.log('[FormicariumLayer] WebGL renderer initialized - use antWebGLRenderer.getDebugInfo() to inspect');
    console.log('[FormicariumLayer] Call disableWebGL() to compare with Canvas 2D fallback');
  }

  /**
   * Show or hide the WebGL canvas for this colony.
   * Called when switching between colonies.
   */
  private setWebGLVisible(visible: boolean): void {
    if (this.webglRenderer) {
      const canvas = this.webglRenderer.getCanvas();
      canvas.style.display = visible ? 'block' : 'none';
      if (visible) {
        console.log(`[FormicariumLayer ${this.colonyIndex}] WebGL canvas shown`);
      }
    }
  }

  /**
   * Render ants using WebGL instanced rendering.
   */
  private renderAntsWebGL(ctx: CanvasRenderingContext2D): void {
    if (!this.webglRenderer) return;

    // Set transform to match Canvas 2D
    this.webglRenderer.setTransform(this.offsetX, this.offsetY, this.scale);

    // Begin frame
    this.webglRenderer.beginFormicariumFrame();

    // Add all ants
    for (const ant of this.sim.ants.values()) {
      if (!ant.isAlive()) continue;
      if (this.sim.antsOnSurface.has(ant.id)) continue;

      // Calculate angle
      let angle = 0;
      const inTunnel = ant.currentTunnelId !== null;

      if (inTunnel) {
        const tunnel = this.sim.tunnelNetwork.getTunnelById(ant.currentTunnelId!);
        if (tunnel) {
          const fromPos = ant.movingTowardEnd ? tunnel.start : tunnel.end;
          const toPos = ant.movingTowardEnd ? tunnel.end : tunnel.start;
          angle = Math.atan2(toPos.y - fromPos.y, toPos.x - fromPos.x);
        }
      } else if (ant.targetPosition) {
        const dirX = ant.targetPosition.x - ant.position.x;
        const dirY = ant.targetPosition.y - ant.position.y;
        if (Math.abs(dirX) > 0.1 || Math.abs(dirY) > 0.1) {
          angle = Math.atan2(dirY, dirX);
        }
      }

      // Determine role key for sprite selection
      let roleKey: AntRole | 'selected' | 'hovered' = ant.role;
      if (ant === this.selectedAnt) {
        roleKey = 'selected';
      } else if (ant === this.hoveredAnt) {
        roleKey = 'hovered';
      }

      this.webglRenderer.addFormicariumAnt(
        ant.position.x,
        ant.position.y,
        angle,
        roleKey,
        1.0 // alpha
      );
    }

    // Render all ants in one draw call
    this.webglRenderer.endFrame();

    // Draw overlays (food, eggs, effects) on Canvas 2D - these are dynamic/varied
    this.renderAntOverlays(ctx);
  }

  /**
   * Render ant overlays (food, eggs, special effects) on Canvas 2D.
   * WebGL renders base ant sprites; this adds the dynamic elements.
   */
  private renderAntOverlays(ctx: CanvasRenderingContext2D): void {
    for (const ant of this.sim.ants.values()) {
      if (!ant.isAlive()) continue;
      if (this.sim.antsOnSurface.has(ant.id)) continue;

      const x = ant.position.x;
      const y = ant.position.y;
      const size = 4 * ant.size;

      // Calculate angle for positioning overlays
      let angle = 0;
      const inTunnel = ant.currentTunnelId !== null;

      if (inTunnel) {
        const tunnel = this.sim.tunnelNetwork.getTunnelById(ant.currentTunnelId!);
        if (tunnel) {
          const fromPos = ant.movingTowardEnd ? tunnel.start : tunnel.end;
          const toPos = ant.movingTowardEnd ? tunnel.end : tunnel.start;
          angle = Math.atan2(toPos.y - fromPos.y, toPos.x - fromPos.x);
        }
      } else if (ant.targetPosition) {
        const dirX = ant.targetPosition.x - ant.position.x;
        const dirY = ant.targetPosition.y - ant.position.y;
        if (Math.abs(dirX) > 0.1 || Math.abs(dirY) > 0.1) {
          angle = Math.atan2(dirY, dirX);
        }
      }

      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const abdomenX = x - cos * size * 1.3;
      const abdomenY = y - sin * size * 1.3;
      const headX = x + cos * size * 1.1;
      const headY = y + sin * size * 1.1;

      // Food indicator
      if (ant.carryingFood && ant.carriedFoodAmount > 0) {
        const foodRGB = this.hueToFoodRGB(ant.carriedFoodHue);
        ctx.fillStyle = `rgb(${foodRGB.r}, ${foodRGB.g}, ${foodRGB.b})`;
        ctx.beginPath();
        ctx.arc(abdomenX, abdomenY - size * 1.2, size * 0.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(255, 255, 200, 0.6)';
        ctx.beginPath();
        ctx.arc(abdomenX - size * 0.1, abdomenY - size * 1.3, size * 0.15, 0, Math.PI * 2);
        ctx.fill();
      }

      // Egg indicator
      if (ant.carryingEgg) {
        ctx.fillStyle = '#f5f5dc';
        ctx.beginPath();
        ctx.ellipse(headX, headY - size * 1.0, size * 0.5, size * 0.35, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.beginPath();
        ctx.ellipse(headX - size * 0.1, headY - size * 1.1, size * 0.25, size * 0.15, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      // Scar indicator
      if (ant.scarred) {
        ctx.strokeStyle = '#2a1008';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x - size * 0.6, y - size * 0.4);
        ctx.lineTo(x + size * 0.4, y + size * 0.4);
        ctx.stroke();
      }

      // Contemplating indicator
      if (ant.state === AntState.CONTEMPLATING) {
        ctx.fillStyle = 'rgba(255, 255, 200, 0.4)';
        ctx.beginPath();
        ctx.arc(headX + cos * size * 0.8, headY - size * 1.5, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(headX + cos * size * 0.5, headY - size * 1.0, 2, 0, Math.PI * 2);
        ctx.fill();
      }

      // Transitioning indicator
      if (ant.task === AntTask.TRANSITIONING) {
        ctx.strokeStyle = '#b8860b';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, size * 2, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  }

  private renderDebris(ctx: CanvasRenderingContext2D): void {
    for (const p of this.sim.getDebrisParticles()) {
      const alpha = Math.min(1, p.life / 500); // Fade out in last 500ms
      // RENDER-ONLY: shimmer on debris. Touches no simulation state, so it may
      // stay on the ambient generator -- a headless run never calls render().
      const shade = Math.floor(40 + Math.random() * 30);
      ctx.fillStyle = `rgba(${shade}, ${shade * 0.6}, ${shade * 0.3}, ${alpha})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * alpha, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderPortals(ctx: CanvasRenderingContext2D): void {
    for (const portal of this.sim.portals) {
      if (!portal.active) continue;

      ctx.save();

      // Draw portal glow
      const gradient = ctx.createRadialGradient(
        portal.position.x, portal.position.y, 0,
        portal.position.x, portal.position.y, portal.radius || 20
      );
      gradient.addColorStop(0, 'rgba(200, 180, 100, 0.3)');
      gradient.addColorStop(1, 'rgba(200, 180, 100, 0)');

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(portal.position.x, portal.position.y, portal.radius || 20, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    }
  }

  private renderTunnels(ctx: CanvasRenderingContext2D): void {
    for (const tunnel of this.sim.tunnelNetwork.getAllTunnels()) {
      if (tunnel.collapsed) {
        // Collapsed tunnels: very visible damage
        ctx.save();

        // Draw broken/cracked tunnel with rubble
        ctx.strokeStyle = '#1a0a05'; // Very dark
        ctx.lineWidth = tunnel.width;
        ctx.lineCap = 'round';
        ctx.setLineDash([3, 8]); // Broken look

        ctx.beginPath();
        ctx.moveTo(tunnel.start.x, tunnel.start.y);
        ctx.lineTo(tunnel.end.x, tunnel.end.y);
        ctx.stroke();

        // Add rubble/debris along the tunnel
        const midX = (tunnel.start.x + tunnel.end.x) / 2;
        const midY = (tunnel.start.y + tunnel.end.y) / 2;
        const len = Math.sqrt(
          Math.pow(tunnel.end.x - tunnel.start.x, 2) +
          Math.pow(tunnel.end.y - tunnel.start.y, 2)
        );

        // Draw rubble rocks
        ctx.fillStyle = '#3d2817';
        const rubbleCount = Math.floor(len / 15);
        for (let i = 0; i < rubbleCount; i++) {
          const t = (i + 0.5) / rubbleCount;
          const rx = tunnel.start.x + (tunnel.end.x - tunnel.start.x) * t;
          const ry = tunnel.start.y + (tunnel.end.y - tunnel.start.y) * t;
          const offset = (Math.sin(i * 7.3) * 0.5) * tunnel.width;

          ctx.beginPath();
          ctx.arc(rx + offset, ry + offset * 0.5, 2 + Math.abs(Math.sin(i * 3.7)) * 3, 0, Math.PI * 2);
          ctx.fill();
        }

        // Draw X marks to indicate blockage
        ctx.strokeStyle = '#8b0000'; // Dark red
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        const xSize = 6;
        ctx.beginPath();
        ctx.moveTo(midX - xSize, midY - xSize);
        ctx.lineTo(midX + xSize, midY + xSize);
        ctx.moveTo(midX + xSize, midY - xSize);
        ctx.lineTo(midX - xSize, midY + xSize);
        ctx.stroke();

        ctx.restore();
      } else {
        // Base color with pheromone glow
        const baseR = 74, baseG = 48, baseB = 32;
        const glowR = 139, glowG = 105, glowB = 20;
        const pheromone = tunnel.pheromoneLevel;

        // Interpolate color based on pheromone level
        const r = Math.round(baseR + (glowR - baseR) * pheromone * 0.5);
        const g = Math.round(baseG + (glowG - baseG) * pheromone * 0.5);
        const b = Math.round(baseB + (glowB - baseB) * pheromone * 0.5);

        ctx.strokeStyle = `rgb(${r}, ${g}, ${b})`;
        ctx.setLineDash([]);

        ctx.lineWidth = tunnel.width;
        ctx.lineCap = 'round';

        ctx.beginPath();
        ctx.moveTo(tunnel.start.x, tunnel.start.y);
        ctx.lineTo(tunnel.end.x, tunnel.end.y);
        ctx.stroke();
      }
    }
    ctx.setLineDash([]);
  }

  private renderChambers(ctx: CanvasRenderingContext2D): void {
    for (const chamber of this.sim.tunnelNetwork.getAllChambers()) {
      if (chamber.destroyed) {
        ctx.save();

        // Draw collapsed/caved-in chamber
        ctx.fillStyle = '#1a0a05'; // Very dark
        ctx.beginPath();
        ctx.ellipse(
          chamber.position.x,
          chamber.position.y,
          chamber.radius,
          chamber.radius * 0.7,
          0,
          0,
          Math.PI * 2
        );
        ctx.fill();

        // Draw rubble/debris inside
        ctx.fillStyle = '#3d2817';
        const debrisCount = Math.floor(chamber.radius / 3);
        for (let i = 0; i < debrisCount; i++) {
          const angle = (i / debrisCount) * Math.PI * 2 + i * 0.7;
          // RENDER-ONLY: rubble scatter inside a destroyed chamber.
          const dist = Math.random() * chamber.radius * 0.7;
          const dx = Math.cos(angle) * dist;
          const dy = Math.sin(angle) * dist * 0.7;
          const size = 2 + Math.random() * 4;

          ctx.beginPath();
          ctx.arc(chamber.position.x + dx, chamber.position.y + dy, size, 0, Math.PI * 2);
          ctx.fill();
        }

        // Draw cracks radiating from center
        ctx.strokeStyle = '#0a0502';
        ctx.lineWidth = 1;
        for (let i = 0; i < 5; i++) {
          const angle = (i / 5) * Math.PI * 2 + 0.3;
          ctx.beginPath();
          ctx.moveTo(chamber.position.x, chamber.position.y);
          ctx.lineTo(
            chamber.position.x + Math.cos(angle) * chamber.radius * 0.9,
            chamber.position.y + Math.sin(angle) * chamber.radius * 0.6
          );
          ctx.stroke();
        }

        // Draw large red X
        ctx.strokeStyle = '#8b0000';
        ctx.lineWidth = 3;
        const xSize = chamber.radius * 0.5;
        ctx.beginPath();
        ctx.moveTo(chamber.position.x - xSize, chamber.position.y - xSize * 0.7);
        ctx.lineTo(chamber.position.x + xSize, chamber.position.y + xSize * 0.7);
        ctx.moveTo(chamber.position.x + xSize, chamber.position.y - xSize * 0.7);
        ctx.lineTo(chamber.position.x - xSize, chamber.position.y + xSize * 0.7);
        ctx.stroke();

        // Label as destroyed
        ctx.fillStyle = '#8b0000';
        ctx.font = 'bold 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('DESTROYED', chamber.position.x, chamber.position.y + chamber.radius + 12);

        ctx.restore();
      } else {
        ctx.fillStyle = this.getChamberColor(chamber);

        ctx.beginPath();
        ctx.ellipse(
          chamber.position.x,
          chamber.position.y,
          chamber.radius,
          chamber.radius * 0.7,
          0,
          0,
          Math.PI * 2
        );
        ctx.fill();

        // Draw food pile for any chamber with food stored
        if (chamber.foodStored > 0) {
          this.renderFoodPile(ctx, chamber);
        }

        // No labels - let graphics show what's happening
      }
    }
  }

  /**
   * Render a food pile in a food storage chamber.
   * The pile size scales with the amount of food stored.
   */
  /**
   * Convert food hue (0-40) to RGB color values.
   * Hue 0 = ruby red, 20 = tangerine orange, 40 = golden yellow
   */
  private hueToFoodRGB(hue: number): { r: number; g: number; b: number } {
    if (hue < 15) {
      // Ruby red / crimson
      return { r: 220, g: 20 + hue * 3, b: 60 };
    } else if (hue < 30) {
      // Tangerine orange
      return { r: 255, g: 100 + (hue - 15) * 6, b: 30 };
    } else {
      // Golden yellow
      return { r: 255, g: 200 + (hue - 30) * 2, b: 50 };
    }
  }

  private renderFoodPile(ctx: CanvasRenderingContext2D, chamber: Chamber): void {
    const foodAmount = chamber.foodStored ?? 0;
    if (foodAmount <= 0) return;

    const fillRatio = Math.min(1, foodAmount / chamber.foodCapacity);

    // Scale pile size based on food amount (20% to 80% of chamber radius)
    const maxPileRadius = chamber.radius * 0.8;
    const minPileRadius = chamber.radius * 0.2;
    const pileRadius = minPileRadius + (maxPileRadius - minPileRadius) * fillRatio;

    const cx = chamber.position.x;
    const cy = chamber.position.y;

    // Get base color from chamber's accumulated food hue
    const baseRGB = this.hueToFoodRGB(chamber.foodHue ?? 20);
    const { r, g, b } = baseRGB;

    // Draw a base mound shape first
    const moundGradient = ctx.createRadialGradient(
      cx, cy - pileRadius * 0.2, 0,
      cx, cy, pileRadius
    );
    moundGradient.addColorStop(0, `rgb(${r}, ${g}, ${b})`);
    moundGradient.addColorStop(0.5, `rgb(${Math.floor(r * 0.8)}, ${Math.floor(g * 0.8)}, ${Math.floor(b * 0.8)})`);
    moundGradient.addColorStop(1, `rgb(${Math.floor(r * 0.6)}, ${Math.floor(g * 0.6)}, ${Math.floor(b * 0.6)})`);
    ctx.fillStyle = moundGradient;
    ctx.beginPath();
    ctx.ellipse(cx, cy, pileRadius, pileRadius * 0.6, 0, 0, Math.PI * 2);
    ctx.fill();

    // Draw multiple overlapping food "nuggets" on top for texture
    const nuggetCount = Math.max(5, Math.floor(8 + fillRatio * 12));

    // Use seeded random for consistent appearance between frames
    const seed = chamber.id * 1000;

    for (let i = 0; i < nuggetCount; i++) {
      // Pseudo-random positioning based on seed
      const angle = ((seed + i * 137.5) % 360) * (Math.PI / 180);
      const dist = ((seed + i * 73) % 100) / 100 * pileRadius * 0.7;
      const nuggetX = cx + Math.cos(angle) * dist;
      const nuggetY = cy + Math.sin(angle) * dist * 0.5; // Flatten for perspective

      // Nugget size - small pellets (2-5 pixels based on fill)
      const baseSize = 2 + fillRatio * 1.5;
      const nuggetSize = baseSize + ((seed + i * 31) % 100) / 100 * baseSize * 0.5;

      // Vary hue slightly per nugget (+/- 5 from base hue)
      const nuggetHueVariation = ((seed + i * 17) % 10) - 5;
      const nuggetHue = Math.max(0, Math.min(40, (chamber.foodHue ?? 20) + nuggetHueVariation));
      const nuggetRGB = this.hueToFoodRGB(nuggetHue);
      ctx.fillStyle = `rgb(${nuggetRGB.r}, ${nuggetRGB.g}, ${nuggetRGB.b})`;

      ctx.beginPath();
      ctx.ellipse(nuggetX, nuggetY, nuggetSize, nuggetSize * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();

      // Add highlight to each nugget
      ctx.fillStyle = 'rgba(255, 255, 200, 0.3)';
      ctx.beginPath();
      ctx.ellipse(nuggetX - nuggetSize * 0.2, nuggetY - nuggetSize * 0.15, nuggetSize * 0.4, nuggetSize * 0.25, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Add top highlight for 3D effect
    const topGradient = ctx.createRadialGradient(
      cx - pileRadius * 0.3, cy - pileRadius * 0.2, 0,
      cx, cy, pileRadius * 0.6
    );
    topGradient.addColorStop(0, `rgba(255, ${Math.floor(g * 1.1)}, ${Math.floor(b * 1.5)}, 0.4)`);
    topGradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
    ctx.fillStyle = topGradient;
    ctx.beginPath();
    ctx.ellipse(cx, cy, pileRadius * 0.6, pileRadius * 0.4, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  private getChamberColor(chamber: Chamber): string {
    switch (chamber.type) {
      case ChamberType.QUEEN_CHAMBER:
        return '#5a3d2a';
      case ChamberType.NURSERY:
        return '#4a3525';
      case ChamberType.FOOD_STORAGE:
        // Color based on fill level
        const fillLevel = (chamber.foodStored ?? 0) / 1000;
        const baseR = 61, baseG = 48, baseB = 32;
        const fullR = 100, fullG = 80, fullB = 40;
        const r = Math.round(baseR + (fullR - baseR) * fillLevel);
        const g = Math.round(baseG + (fullG - baseG) * fillLevel);
        const b = Math.round(baseB + (fullB - baseB) * fillLevel);
        return `rgb(${r}, ${g}, ${b})`;
      case ChamberType.ENTRANCE:
        return '#5a4a30';
      case ChamberType.MIDDEN:
        return '#2a2015';
      default:
        return '#3a2a1a';
    }
  }
  /**
   * Render brood (eggs, larvae, pupae) in the colony.
   */
  private renderBrood(ctx: CanvasRenderingContext2D): void {
    for (const brood of this.sim.brood.values()) {
      // Skip brood being carried (will be rendered with the ant)
      if (brood.carriedByAntId !== null) continue;

      const x = brood.position.x;
      const y = brood.position.y;

      switch (brood.stage) {
        case BroodStage.EGG:
          // Eggs are small, white, oval
          ctx.fillStyle = '#f5f5dc'; // Beige/cream color
          ctx.beginPath();
          ctx.ellipse(x, y, 3, 2, 0, 0, Math.PI * 2);
          ctx.fill();
          // Subtle highlight
          ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
          ctx.beginPath();
          ctx.ellipse(x - 0.5, y - 0.5, 1.5, 1, 0, 0, Math.PI * 2);
          ctx.fill();
          break;

        case BroodStage.LARVA:
          // Larvae are larger, curved, yellowish-white grubs
          const feedRatio = brood.fed / this.sim.larvaFoodNeeded;
          const larvaSize = 4 + feedRatio * 3; // Grow as they're fed

          // Body color gets more yellow/healthy as fed
          const r = Math.round(245 - feedRatio * 20);
          const g = Math.round(235 - feedRatio * 10);
          const b = Math.round(200 - feedRatio * 50);
          ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;

          // Draw curved grub body (3 segments)
          ctx.beginPath();
          ctx.ellipse(x - larvaSize * 0.4, y, larvaSize * 0.5, larvaSize * 0.4, -0.3, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.ellipse(x, y, larvaSize * 0.6, larvaSize * 0.5, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.ellipse(x + larvaSize * 0.5, y + larvaSize * 0.1, larvaSize * 0.4, larvaSize * 0.35, 0.2, 0, Math.PI * 2);
          ctx.fill();

          // Highlight
          ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
          ctx.beginPath();
          ctx.ellipse(x - larvaSize * 0.1, y - larvaSize * 0.2, larvaSize * 0.3, larvaSize * 0.2, 0, 0, Math.PI * 2);
          ctx.fill();
          break;

        case BroodStage.PUPA:
          // Pupae are oval, darker, with visible ant shape forming
          ctx.fillStyle = '#d2b48c'; // Tan color
          ctx.beginPath();
          ctx.ellipse(x, y, 5, 3, 0, 0, Math.PI * 2);
          ctx.fill();

          // Segment lines to show developing ant
          ctx.strokeStyle = '#a0826d';
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(x - 2, y - 2);
          ctx.lineTo(x - 2, y + 2);
          ctx.moveTo(x + 1, y - 2);
          ctx.lineTo(x + 1, y + 2);
          ctx.stroke();

          // Highlight
          ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
          ctx.beginPath();
          ctx.ellipse(x - 1, y - 1, 2, 1.5, 0, 0, Math.PI * 2);
          ctx.fill();
          break;
      }
    }
  }
  private renderAntInfo(ctx: CanvasRenderingContext2D, ant: Ant): void {
    const panelWidth = 240;
    const panelX = this.width - panelWidth - 20;
    const panelY = 60;

    // Build detailed info lines
    const lines: Array<{ text: string; color?: string }> = [];

    // Role with color
    const roleColors: Record<string, string> = {
      queen: '#b8860b',
      forager: '#8b6914',
      nurse: '#7a6520',
      soldier: '#654321',
      engineer: '#6b5a4a'
    };
    lines.push({ text: `Role: ${ant.role.charAt(0).toUpperCase() + ant.role.slice(1)}`, color: roleColors[ant.role] });

    // Age
    lines.push({ text: `Age: ${ant.getAgeInDays()} days${ant.isElderly() ? ' (Elder)' : ''}` });

    // Health
    const healthColor = ant.health > 70 ? '#4a9' : ant.health > 30 ? '#da5' : '#d55';
    lines.push({ text: `Health: ${Math.round(ant.health)}/${ant.maxHealth}`, color: healthColor });

    // Hunger
    const hungerColor = ant.hunger < 30 ? '#4a9' : ant.hunger < 60 ? '#da5' : '#d55';
    lines.push({ text: `Hunger: ${Math.round(ant.hunger)}%`, color: hungerColor });

    // Current task
    const taskName = ant.task.replace(/_/g, ' ').toLowerCase();
    lines.push({ text: `Task: ${taskName || 'idle'}` });

    // Role-specific stats
    if (ant.role === AntRole.FORAGER) {
      lines.push({ text: `Foraging trips: ${ant.stats.foragingTrips}` });
      lines.push({ text: `Food gathered: ${ant.stats.foodGathered} units` });
    } else if (ant.role === AntRole.SOLDIER) {
      lines.push({ text: `Threats repelled: ${ant.stats.threatsRepelled}` });
      if (ant.scarred) lines.push({ text: `Battle-scarred`, color: '#a55' });
    } else if (ant.role === AntRole.NURSE) {
      lines.push({ text: `Brood cared for: ${ant.stats.eggsNursed}` });
    } else if (ant.role === AntRole.ENGINEER) {
      lines.push({ text: `Structures repaired: ${ant.stats.tunnelsDug}` });
    }

    // Distance traveled
    lines.push({ text: `Distance: ${Math.round(ant.stats.distanceTraveled)} units` });

    // Special traits
    if (ant.injured) lines.push({ text: `Injured`, color: '#d55' });
    if (ant.isPhilosopher) lines.push({ text: `Sometimes pauses to think...`, color: '#aaa' });

    const lineHeight = 18;
    const hasNameButton = !ant.customName && ant !== this.sim.getQueen();
    const panelHeight = lines.length * lineHeight + (hasNameButton ? 80 : 50);

    // Background
    ctx.fillStyle = 'rgba(30, 20, 10, 0.95)';
    ctx.strokeStyle = '#5a4510';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(panelX, panelY, panelWidth, panelHeight, 8);
    ctx.fill();
    ctx.stroke();

    // Title with highlight
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 15px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(ant.getDisplayName(), panelX + 15, panelY + 22);

    // Underline
    ctx.strokeStyle = '#5a4510';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(panelX + 15, panelY + 30);
    ctx.lineTo(panelX + panelWidth - 15, panelY + 30);
    ctx.stroke();

    // Info lines
    ctx.font = '12px "Segoe UI", system-ui, sans-serif';
    lines.forEach((line, i) => {
      ctx.fillStyle = line.color || '#a08050';
      ctx.fillText(line.text, panelX + 15, panelY + 48 + i * lineHeight);
    });

    // Name button if not custom-named
    if (hasNameButton) {
      const btnY = panelY + panelHeight - 32;
      const btnX = panelX + 15;
      const btnWidth = panelWidth - 30;

      // Button background with hover effect
      const isHoveringBtn = this.mouseX >= btnX && this.mouseX <= btnX + btnWidth &&
                            this.mouseY >= btnY && this.mouseY <= btnY + 24;
      ctx.fillStyle = isHoveringBtn ? '#5a4a2a' : '#3a2a1a';
      ctx.strokeStyle = '#6a5a3a';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(btnX, btnY, btnWidth, 24, 4);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = isHoveringBtn ? '#daa520' : '#8b6914';
      ctx.font = '12px "Segoe UI", system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Click to name this ant', panelX + panelWidth / 2, btnY + 16);
    }
  }

  private renderAntHover(ctx: CanvasRenderingContext2D, ant: Ant): void {
    const worldX = ant.position.x * this.scale + this.offsetX;
    const worldY = ant.position.y * this.scale + this.offsetY;

    // Draw highlight ring around ant
    ctx.save();
    ctx.strokeStyle = '#ffd700';
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.arc(worldX, worldY, 12 * this.scale, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Tooltip with more info
    const name = ant.getDisplayName();
    const role = ant.role.charAt(0).toUpperCase() + ant.role.slice(1);
    const task = ant.task.replace(/_/g, ' ').toLowerCase() || 'idle';

    const tooltipWidth = 140;
    const tooltipHeight = 50;
    const tooltipX = worldX + 15;
    const tooltipY = worldY - tooltipHeight - 5;

    // Background
    ctx.fillStyle = 'rgba(30, 20, 10, 0.95)';
    ctx.strokeStyle = '#daa520';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight, 6);
    ctx.fill();
    ctx.stroke();

    // Name
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 12px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(name, tooltipX + 10, tooltipY + 16);

    // Role and task
    ctx.fillStyle = '#a08050';
    ctx.font = '11px "Segoe UI", system-ui, sans-serif';
    ctx.fillText(`${role}`, tooltipX + 10, tooltipY + 32);
    ctx.fillStyle = '#888';
    ctx.fillText(`${task}`, tooltipX + 10, tooltipY + 45);
  }
  resize(dimensions: Dimensions): void {
    super.resize(dimensions);

    // Adjust scale based on viewport - use smaller buffer on small screens
    const buffer = Math.min(100, this.width * 0.1, this.height * 0.1);
    this.baseScale = Math.min(
      this.width / (this.sim.tunnelNetwork.width + buffer),
      this.height / (this.sim.tunnelNetwork.height + buffer)
    );
    if (!this.hasUserTransformed) {
      this.scale = this.baseScale;
      this.centerView();
    } else {
      this.scale = Math.max(this.getMinScale(), Math.min(this.getMaxScale(), this.scale));
    }

    // Resize WebGL canvas if initialized
    if (this.webglRenderer) {
      this.webglRenderer.resize(this.width, this.height, window.devicePixelRatio || 1);
    }
  }

  // --- Simulation forwarding ------------------------------------------------
  //
  // The colony's own API, reachable through the object the client already holds.
  // Nothing here decides anything; every one of these is `this.sim`.

  update(dt: number): void { this.sim.update(dt); }

  getPortals(): Portal[] { return this.sim.portals; }
  receiveEntity(entity: TransferableEntity, fromPortal: string): boolean {
    return this.sim.receiveEntity(entity, fromPortal);
  }
  confirmAntOnSurface(antId: number): void { this.sim.confirmAntOnSurface(antId); }
  setSurfacePortalPosition(gridX: number, gridY: number): void {
    this.sim.setSurfacePortalPosition(gridX, gridY);
  }

  getStats(): ColonyStats { return this.sim.getStats(); }
  getPopulation(): number { return this.sim.getPopulation(); }
  getLastSurvivor(): string | null { return this.sim.getLastSurvivor(); }
  getAntsOnSurfaceCount(): number { return this.sim.getAntsOnSurfaceCount(); }
  getTunnelNetwork(): TunnelNetwork { return this.sim.tunnelNetwork; }
  getColonyIndex(): number { return this.colonyIndex; }
  getTerritoryPercentage(): number { return this.sim.getTerritoryPercentage(); }
  getTotalFood(): number { return this.sim.getTotalFood(); }
  getForagingUrgencyPublic(): number { return this.sim.getForagingUrgencyPublic(); }
  getForagersOutCount(): number { return this.sim.getForagersOutCount(); }
  getBroodCounts(): { eggs: number; larvae: number; pupae: number } { return this.sim.getBroodCounts(); }
  getQueenHungerPercent(): number { return this.sim.getQueenHungerPercent(); }
  getRecentNews(count: number): Array<{ message: string; type: string }> {
    return this.sim.getRecentNews(count);
  }

  /**
   * The season's document, and the two ways back from one.
   *
   * View state is the layer's to forget: an ant the player had selected may not
   * exist in the world being restored, and a stale reference would draw a
   * biography for an ant nobody can find.
   */
  snapshot(): ColonySnapshot { return this.sim.snapshot(); }

  restore(state: ColonySnapshot): void {
    this.sim.restore(state);
    this.clearSelection();
  }

  restoreFromFile(state: ColonySnapshot): void {
    this.sim.restoreFromFile(state);
    this.clearSelection();
  }

  restart(): void {
    this.sim.restart();
    this.clearSelection();
  }

  private clearSelection(): void {
    this.selectedAnt = null;
    this.hoveredAnt = null;
  }

  updateMetabolismConfig(partial: Partial<MetabolismConfig>): void { this.sim.updateMetabolismConfig(partial); }
  updateBroodConfig(partial: Partial<BroodSystemConfig>): void { this.sim.updateBroodConfig(partial); }
  updateStimulusConfig(partial: Partial<StimulusConfig>): void { this.sim.updateStimulusConfig(partial); }
  updateRoleTransitionConfig(partial: Partial<RoleTransitionConfig>): void { this.sim.updateRoleTransitionConfig(partial); }
  updateColonyConfig(partial: Record<string, number>): void { this.sim.updateColonyConfig(partial); }
  updateDamageConfig(partial: Partial<DamageConfig>): void { this.sim.updateDamageConfig(partial); }
  setRoleAttributes(attrs: Record<AntRole, AntRoleAttributes>): void { this.sim.setRoleAttributes(attrs); }
  getRoleAttributes(): Record<AntRole, AntRoleAttributes> { return this.sim.getRoleAttributes(); }
}

// Re-export the old Colony name for backwards compatibility
export { FormicariumLayer as Colony };
